import { Badge, Button } from "@mantine/core";
import { DataTableColumn } from "mantine-datatable";
import { useMemo } from "react";

import { TableData, TableRow } from "../../../components/Table/types";
import { toLocalDate } from "../../../utils/date";
import { toHumanReadable } from "../../../utils/string";
import { KYCEntry, KYCStatus } from "../types";

interface Props {
  data: KYCEntry[] | null | undefined;
}

export type ExtendedKYCTableRow = TableRow & {
  rawStatus: KYCStatus;
  companyName: string;
  customerId: number;
  createdAtRaw: string;
  errorReason?: string;
};

export function useKYCTable({ data }: Props): TableData {
  const columns: DataTableColumn<TableRow>[] = [
    {
      accessor: "id",
      hidden: true,
    },
    {
      accessor: "requestDate",
      title: "Request Date",
      sortable: true,
      width: 200,
    },
    {
      accessor: "companyName",
      title: "Company Name",
      sortable: true,
      width: 350,
    },
    {
      accessor: "result",
      title: "Result",
      sortable: false,
      width: 200,
    },
    {
      accessor: "status",
      title: "Status",
      sortable: true,
      width: 200,
    },
  ];

  const getStatusBadge = (status: KYCStatus | string) => {
    const statusConfig: Record<string, { color: string }> = {
      [KYCStatus.COMPLETED]: {
        color: "green",
      },
      [KYCStatus.ONGOING]: {
        color: "blue",
      },
      [KYCStatus.FAILED]: {
        color: "red",
      },
      [KYCStatus.REJECTED]: {
        color: "orange",
      },
    };

    const config = statusConfig[status] ?? { color: "gray" };
    return (
      <Badge color={config.color} size="sm">
        {toHumanReadable(status)}
      </Badge>
    );
  };

  const rows = useMemo(() => {
    if (!data) return [];

    return data.map(
      (item): ExtendedKYCTableRow => ({
        id: item.id,
        requestDate: toLocalDate(item.requestDate),
        companyName: item.companyName,
        errorReason: item?.error as string | undefined,
        result: (
          <Button
            variant="filled"
            size="sm"
            style={{
              backgroundColor:
                item.status === KYCStatus.ONGOING ? "#6b7280" : "#1a2332",
              color: "white",
            }}
          >
            {item.status === KYCStatus.ONGOING ? "Processing" : "View Result"}
          </Button>
        ),
        status: getStatusBadge(item.status),
        rawStatus: item.status,
        customerId: item.customerId,
        createdAtRaw: item.createdAt,
      })
    ) as TableRow[];
  }, [data]);

  return [columns, rows];
}
